import axios from 'axios';

const isLocal = process.env.REACT_APP_IS_LOCAL === 'true';

export const API_BASE_URL = isLocal
  ? (process.env.REACT_APP_LOCAL_HOST_URL || 'http://localhost:5000/api')
  : (process.env.REACT_APP_PROD_HOST_URL || 'https://kanjusee.com/api');

export const SOCKET_URL = API_BASE_URL.replace(/\/api$/, '');

const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('kanjusee_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      const isExempt = err.config?.url?.endsWith('/auth/login') || err.config?.url?.endsWith('/auth/password');
      if (!isExempt) {
        localStorage.removeItem('kanjusee_token');
        if (window.location.pathname.startsWith('/vendor')) {
          window.location.href = '/vendor/login';
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  checkEmail: email => api.get(`/auth/check-email?email=${encodeURIComponent(email)}`),
  register: data => api.post('/auth/register', data),
  verifyRegistration: data => api.post('/auth/verify-registration', data),
  forgotPassword: data => api.post('/auth/forgot-password', data),
  resetPassword: data => api.post('/auth/reset-password', data),
  login: data => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: data => api.put('/auth/profile', data),
  changePassword: data => api.put('/auth/password', data),
  deleteAccount: () => api.delete('/auth/delete-account'),
};

export const itemAPI = {
  list: params => api.get('/items', { params }),
  facets: params => api.get('/items/facets', { params }),
  get: id => api.get(`/items/${id}`),
  bySlug: slug => api.get(`/items/slug/${encodeURIComponent(slug)}`),
  create: data => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  remove: id => api.delete(`/items/${id}`),
};

export const locationAPI = {
  all: () => api.get('/locations'),
  states: () => api.get('/locations/states'),
  cities: state => api.get(`/locations/cities/${encodeURIComponent(state)}`),
};

export const categoryAPI = {
  tree: (params) => api.get('/categories', { params }),
  flat: (params) => api.get('/categories/flat', { params }),
  children: (id, params) => api.get(`/categories/${id}/children`, { params }),
  bySlug: slug => api.get(`/categories/slug/${encodeURIComponent(slug)}`),
  subBySlug: slug => api.get(`/categories/subcategory/slug/${encodeURIComponent(slug)}`),
  // Category CRUD
  createCategory: data => api.post('/categories/category', data),
  updateCategory: (id, data) => api.put(`/categories/category/${id}`, data),
  removeCategory: id => api.delete(`/categories/category/${id}`),
  // SubCategory CRUD
  createSubcategory: data => api.post('/categories/subcategory', data),
  updateSubcategory: (id, data) => api.put(`/categories/subcategory/${id}`, data),
  removeSubcategory: id => api.delete(`/categories/subcategory/${id}`),
  mapSubcategory: (id, categoryId) => api.post(`/categories/subcategory/${id}/map`, { categoryId }),
  // Brand CRUD
  createBrand: data => api.post('/categories/brand', data),
  updateBrand: (id, data) => api.put(`/categories/brand/${id}`, data),
  removeBrand: id => api.delete(`/categories/brand/${id}`),
  mapBrand: (id, subCategoryId) => api.post(`/categories/brand/${id}/map`, { subCategoryId }),
  seedBrandLogos: () => api.post('/categories/seed-brand-logos'),
  // Legacy compat
  create: data => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  remove: id => api.delete(`/categories/${id}`),
};

export const attributeAPI = {
  resolve: params => api.get('/attributes/resolve', { params }),
  adminList: () => api.get('/attributes/admin'),
  adminSave: (scope, refId, attributes) => api.put(`/attributes/admin/${scope}/${refId}`, { attributes }),
  generate: onlyMissing => api.post('/attributes/admin/generate', { onlyMissing }),
  genStatus: () => api.get('/attributes/admin/status'),
};

export const aiSetupAPI = {
  status: () => api.get('/admin/ai-setup'),
  save: data => api.put('/admin/ai-setup', data),
  test: () => api.post('/admin/ai-setup/test'),
  clear: () => api.delete('/admin/ai-setup'),
};

export const demandAPI = {
  create: data => api.post('/demands', data),
  list: params => api.get('/demands', { params }),
  mine: () => api.get('/demands/mine'),
  get: id => api.get(`/demands/${id}`),
  close: id => api.put(`/demands/${id}/close`),
  publicList: params => api.get('/demands/public', { params }),
  publicGrouped: () => api.get('/demands/public-grouped'),
};

export const offerAPI = {
  submit: data => api.post('/offers', data),
  byDemand: demandId => api.get(`/offers/demand/${demandId}`),
  mine: () => api.get('/offers/mine'),
  accept: id => api.put(`/offers/${id}/accept`),
  reject: id => api.put(`/offers/${id}/reject`),
};

export const vendorProfileAPI = {
  create: data => api.post('/vendor-profiles', data),
  me: () => api.get('/vendor-profiles/me'),
  update: data => api.put('/vendor-profiles/me', data),
  get: id => api.get(`/vendor-profiles/${id}`),
  submitKyc: () => api.post('/vendor-profiles/me/kyc/submit'),
  // Admin
  adminListKyc: status => api.get('/vendor-profiles/admin/kyc', { params: status ? { status } : {} }),
  adminApproveKyc: id => api.post(`/vendor-profiles/admin/${id}/kyc/approve`),
  adminRejectKyc: (id, reason) => api.post(`/vendor-profiles/admin/${id}/kyc/reject`, { reason }),
  createKycOrder: () => api.post('/vendor-profiles/me/kyc/create-order'),
  verifyKycPayment: data => api.post('/vendor-profiles/me/kyc/verify-payment', data),
};

export const leadFilterAPI = {
  create: data => api.post('/lead-filters', data),
  list: () => api.get('/lead-filters'),
  update: (id, data) => api.put(`/lead-filters/${id}`, data),
  remove: id => api.delete(`/lead-filters/${id}`),
  toggle: id => api.put(`/lead-filters/${id}/toggle`),
  stats: () => api.get('/lead-filters/stats'),
  audit: id => api.get(`/lead-filters/${id}/audit`),
};

export const leadPricingAPI = {
  list: () => api.get('/lead-pricing'),
  publicList: () => api.get('/lead-pricing/public'),
  create: data => api.post('/lead-pricing', data),
  update: (id, data) => api.put(`/lead-pricing/${id}`, data),
  remove: id => api.delete(`/lead-pricing/${id}`),
  getRate: params => api.get('/lead-pricing/rate', { params }),
  getFinancialRate: () => api.get('/lead-pricing/financial-rate'),
  updateFinancialRate: (rate) => api.put('/lead-pricing/financial-rate', { rate }),
};

export const uploadAPI = {
  upload: (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post('/upload', formData);
  },
  uploadItemMedia: (files, onUploadProgress) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post('/upload/item-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  generateImageBase64: (prompt, index) => api.post('/upload/generate-base64', { prompt, index }),
  saveImageBase64: (base64, filename, mimeType) => api.post('/upload/save-base64', { base64, filename, mimeType }),
  searchPhotos: (prompt) => api.post('/upload/search-photos', { prompt }),
  downloadUrl: (url, name) => api.post('/upload/download-url', { url, name }),
};

export const walletAPI = {
  me: () => api.get('/wallets/me'),
  recharge: data => api.post('/wallets/recharge', data),
  transactions: params => api.get('/wallets/transactions', { params }),
  requestCredit: () => api.post('/wallets/request-credit'),
};

export const razorpayAPI = {
  // Step 1 — create order on backend, returns { orderId, keyId, amount, ... }
  createOrder: data => api.post('/wallets/razorpay/create-order', data),
  // Step 2 — verify HMAC signature, credit wallet, emit invoice
  verifyPayment: data => api.post('/wallets/razorpay/verify', data),
};

export const invoiceAPI = {
  list: () => api.get('/invoices'),
  get: id => api.get(`/invoices/${id}`),
  generateMonthly: data => api.post('/invoices/monthly', data),
  adminAll: params => api.get('/invoices/admin/all', { params }),
};

export const gstRuleAPI = {
  active: () => api.get('/gst-rules/active'),
  list: () => api.get('/gst-rules'),
  create: data => api.post('/gst-rules', data),
  update: (id, data) => api.put(`/gst-rules/${id}`, data),
  activate: id => api.post(`/gst-rules/${id}/activate`),
};

export const bulkAPI = {
  listListings: params => api.get('/bulk/listings', { params }),
  myListings: () => api.get('/bulk/listings/mine'),
  createListing: data => api.post('/bulk/listings', data),
  updateListing: (id, data) => api.put(`/bulk/listings/${id}`, data),
  removeListing: id => api.delete(`/bulk/listings/${id}`),
  placeBid: data => api.post('/bulk/bids', data),
  myBids: () => api.get('/bulk/bids/mine'),
  bidsByListing: id => api.get(`/bulk/bids/listing/${id}`),
  acceptBid: id => api.put(`/bulk/bids/${id}/accept`),
  rejectBid: id => api.put(`/bulk/bids/${id}/reject`),
};

export const geoAnalyticsAPI = {
  filters: () => api.get('/admin/geo-analytics/filters'),
  report: params => api.get('/admin/geo-analytics/report', { params }),
  profiles: () => api.get('/admin/geo-analytics/market-profiles'),
  updateProfile: (key, data) => api.put(`/admin/geo-analytics/market-profiles/${key}`, data),
  resetProfiles: () => api.post('/admin/geo-analytics/market-profiles/reset'),
};

export const vendorDiscoveryAPI = {
  list: params => api.get('/admin/vendor-discovery', { params }),
  plan: params => api.get('/admin/vendor-discovery/plan', { params }),
  run: data => api.post('/admin/vendor-discovery/run', data),
  importSel: ids => api.post('/admin/vendor-discovery/import', { ids }),
  update: (id, data) => api.put(`/admin/vendor-discovery/prospects/${id}`, data),
  reject: (id, reason) => api.put(`/admin/vendor-discovery/prospects/${id}/reject`, { reason }),
};

export const outreachAPI = {
  overview: () => api.get('/admin/outreach'),
  audience: params => api.get('/admin/outreach/audience', { params }),
  send: data => api.post('/admin/outreach/send', data),
  createTemplate: data => api.post('/admin/outreach/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/outreach/templates/${id}`, data),
  deleteTemplate: id => api.delete(`/admin/outreach/templates/${id}`),
  saveLoop: data => api.put('/admin/outreach/loop', data),
  runLoopNow: () => api.post('/admin/outreach/loop/run-now'),
  saveChannels: data => api.put('/admin/outreach/channels', data),
  testChannel: data => api.post('/admin/outreach/channels/test', data),
};

export const imageBackfillAPI = {
  status: () => api.get('/admin/image-backfill'),
  run: () => api.post('/admin/image-backfill/run'),
};

export const discoveryAPI = {
  list: params => api.get('/admin/discovery', { params }),
  run: () => api.post('/admin/discovery/run'),
  settings: data => api.put('/admin/discovery/settings', data),
  approve: (id, data) => api.put(`/admin/discovery/suggestions/${id}/approve`, data || {}),
  reject: (id, reason) => api.put(`/admin/discovery/suggestions/${id}/reject`, { reason }),
  bulk: data => api.post('/admin/discovery/suggestions/bulk', data),
};

export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  vendors: () => api.get('/admin/vendors'),
  createVendor: data => api.post('/admin/vendors', data),
  updateVendorStatus: (id, data) => api.put(`/admin/vendors/${id}/status`, data),
  deleteVendor: id => api.delete(`/admin/vendors/${id}`),
  demands: params => api.get('/admin/demands', { params }),
  moderateDemand: (id, data) => api.put(`/admin/demands/${id}/moderate`, data),
  analytics: () => api.get('/admin/analytics'),
  allWallets: () => api.get('/wallets/admin/all'),
  adjustWallet: (vendorId, data) => api.post(`/wallets/${vendorId}/adjust`, data),
  allInvoices: () => api.get('/invoices/admin/all'),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: data => api.put('/admin/settings', data),
  getCreditRequests: () => api.get('/admin/credit-requests'),
  addCredit: (id, amount) => api.post(`/admin/credit-requests/${id}/add-credit`, { amount }),
  addCreditToAll: amount => api.post('/admin/credit-requests/add-credit-to-all', { amount }),
};

export const notificationAPI = {
  list: params => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: id => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
};

export const emailTemplateAPI = {
  list: params => api.get('/email-templates', { params }),
  create: data => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  remove: id => api.delete(`/email-templates/${id}`),
};

export const feedbackAPI = {
  active: () => api.get('/feedback/active'),
  respond: (id, data) => api.post(`/feedback/${id}/respond`, data),
  getSettings: () => api.get('/feedback/admin/settings'),
  updateSettings: data => api.put('/feedback/admin/settings', data),
  list: params => api.get('/feedback/admin', { params }),
  history: userId => api.get(`/feedback/admin/user/${userId}/history`),
  resend: id => api.post(`/feedback/admin/${id}/resend`),
  resendAll: data => api.post('/feedback/admin/resend-all', data),
};

export const issuesAPI = {
  create: data => api.post('/issues', data),
  list: params => api.get('/issues/admin', { params }),
  userHistory: userId => api.get(`/issues/admin/user/${userId}`),
  fix: id => api.put(`/issues/admin/${id}/fix`),
};

export const chatAPI = {
  rooms: () => api.get('/chat/rooms'),
  roomForOffer: offerId => api.get(`/chat/rooms/offer/${offerId}`),
  roomForDemand: demandId => api.get(`/chat/rooms/demand/${demandId}`),
  messages: roomId => api.get(`/chat/rooms/${roomId}/messages`),
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/chat/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteRoom: roomId => api.delete(`/chat/rooms/${roomId}`),
  deleteMessage: messageId => api.delete(`/chat/messages/${messageId}`),
  markRead: roomId => api.post(`/chat/rooms/${roomId}/read`),
};

export default api;
